import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Check, X, FileCheck } from "lucide-react";
import { Reveal } from "@/components/landing/Reveal";
import { Verify } from "@/components/landing/Verify";
import { Footer } from "@/components/landing/Footer";

const WHAT_WE_VERIFY = [
  "Company registration & operating status", "Directors, shareholders & company history",
  "Registered office & business address", "Recruitment licence / authorisation (where applicable)",
  "VAT & corporate information", "Website & domain verification",
  "Official company email verification", "Recruitment activity & business profile",
  "Employer & job-order verification", "Employment contract / document consistency",
  "Work-permit information & supporting docs", "Payment & invoice details",
  "Bank-account / company-name consistency", "Online reputation & public complaints",
  "Regulatory warnings & enforcement info", "Insolvency / corporate-risk indicators",
  "LinkedIn & professional-profile checks", "Previous business / recruitment references",
  "Identification of inconsistencies & fraud indicators",
];

const RED_FLAGS = [
  "Unverified companies", "Fake or inconsistent documents", "Unclear ownership or identity",
  "Bank accounts of unrelated entities", "Pressure for immediate payments",
  "Unverifiable employment offers", "Unclear work-permit procedures",
  "Repeated delays in providing evidence", "Inconsistent company information",
  "Suspicious websites, domains or emails", "Negative or unexplained business history",
];

const REPORT = [
  "Company Verification", "Ownership & Management Check", "Recruitment Activity Assessment",
  "Document Verification Findings", "Employer / Job Verification Findings", "Red Flags Identified",
  "Payment-Risk Indicators", "Overall Risk Assessment", "Recommended Next Steps",
];

const RISK = [
  ["LOW", "No major concerns identified from the checks performed.", "#16a34a"],
  ["MEDIUM", "Certain concerns require clarification or additional verification.", "#f59e0b"],
  ["HIGH", "Significant warning indicators; proceed only after further investigation.", "#ef4444"],
  ["CRITICAL", "Serious inconsistencies or potential fraud; we recommend not proceeding.", "#0a0a0a"],
];

const WHO = [
  "Recruitment agencies", "Manpower companies", "Immigration consultants",
  "Overseas employment agencies", "B2B recruitment companies", "European recruitment partners",
  "Employers engaging foreign agencies", "Companies sourcing workers from outside Europe",
];

export default function DueDiligencePage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main data-testid="due-diligence-page" className="relative">
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-md border-b border-[#e4e4e7]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <a href="/" data-testid="dd-logo" className="shrink-0">
            <img src="https://customer-assets-lxgj4vgw.emergentagent.net/job_daab-recruit/artifacts/kveh0gcx_file_0000000029048211809fc80c6002540d.webp" alt="DAAB Consulting Recruitment Services" className="h-9 md:h-11 w-auto object-contain" />
          </a>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="dd-back" className="inline-flex items-center gap-2 font-body text-sm text-[#52525b] hover:text-[#0a0a0a]">
              <ArrowLeft size={16} /> Back to site
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-16 md:pt-24 pb-16 px-6 md:px-12 bg-[#0a0a0a] text-white">
        <div className="max-w-[1600px] mx-auto">
          <Reveal>
            <div className="flex items-center gap-3 mb-6"><ShieldCheck size={24} className="text-[#ff3b30]" /><span className="font-body text-sm tracking-[0.25em] uppercase text-[#ff3b30]">Europe recruitment agency due-diligence</span></div>
            <h1 className="font-head uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl xl:text-8xl">
              Verify before<br />you partner.<br /><span className="text-[#52525b]">Verify before you pay.</span>
            </h1>
            <p className="mt-8 font-body text-lg text-[#a1a1aa] max-w-2xl leading-relaxed">
              Before signing a recruitment agreement, paying sponsorship or processing fees, or
              submitting candidate documents, conduct proper due diligence on the European agency,
              employer and recruitment process. Built for agencies, manpower companies, immigration
              consultants and B2B recruitment partners.
            </p>
            <a href="#verify" className="mt-10 inline-flex items-center gap-2 bg-[#ff3b30] hover:bg-[#ff5248] text-white font-head uppercase text-sm tracking-wide px-8 py-4 transition-colors">Request verification</a>
          </Reveal>
        </div>
      </section>

      {/* What we verify */}
      <section className="py-20 md:py-28 px-6 md:px-12">
        <div className="max-w-[1600px] mx-auto">
          <Reveal><h2 className="font-head uppercase tracking-tighter text-4xl md:text-6xl mb-12">What we verify</h2></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10">
            {WHAT_WE_VERIFY.map((it, i) => (
              <Reveal key={it} delay={(i % 3) * 0.05}>
                <div className="flex items-start gap-3 py-4 border-b border-[#e4e4e7] font-body text-[#0a0a0a]">
                  <Check size={18} className="text-[#ff3b30] shrink-0 mt-0.5" strokeWidth={2.5} />{it}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Red flags */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-[#f5f5f4] border-y border-[#e4e4e7]">
        <div className="max-w-[1600px] mx-auto">
          <Reveal><h2 className="font-head uppercase tracking-tighter text-4xl md:text-6xl mb-12">Fraud-risk identification</h2></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
            {RED_FLAGS.map((it, i) => (
              <Reveal key={it} delay={(i % 2) * 0.05}>
                <div className="flex items-start gap-3 py-4 border-b border-[#e4e4e7] font-body text-[#0a0a0a]">
                  <X size={18} className="text-[#ef4444] shrink-0 mt-0.5" strokeWidth={2.5} />{it}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Report + Risk classification */}
      <section className="py-20 md:py-28 px-6 md:px-12">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
          <Reveal>
            <div className="flex items-center gap-3 mb-8"><FileCheck size={24} className="text-[#ff3b30]" /><h2 className="font-head uppercase tracking-tighter text-3xl md:text-5xl">The report</h2></div>
            <div className="border-t border-[#e4e4e7]">
              {REPORT.map((r, i) => (
                <div key={r} className="flex items-center gap-4 py-4 border-b border-[#e4e4e7]">
                  <span className="font-head text-lg text-[#a1a1aa] w-8">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-body text-[#0a0a0a]">{r}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-head uppercase tracking-tighter text-3xl md:text-5xl mb-8">Risk classification</h2>
            <div className="space-y-4">
              {RISK.map(([level, desc, color]) => (
                <div key={level} className="border border-[#e4e4e7] p-6 flex items-start gap-4">
                  <span className="w-4 h-4 rounded-full shrink-0 mt-1" style={{ backgroundColor: color }} />
                  <div>
                    <div className="font-head uppercase tracking-tight text-xl">{level} risk</div>
                    <p className="font-body text-sm text-[#52525b] mt-1">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Who can use */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-[#f5f5f4] border-y border-[#e4e4e7]">
        <div className="max-w-[1600px] mx-auto">
          <Reveal><h2 className="font-head uppercase tracking-tighter text-4xl md:text-6xl mb-12">Who can use this service</h2></Reveal>
          <div className="flex flex-wrap gap-3">
            {WHO.map((w, i) => (
              <Reveal key={w} delay={i * 0.03}>
                <span className="border border-[#e4e4e7] bg-white px-5 py-3 font-head uppercase tracking-tight text-lg">{w}</span>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <blockquote className="mt-16 font-accent italic text-2xl md:text-4xl leading-snug text-[#0a0a0a] max-w-4xl">
              <span className="text-[#ff3b30] not-italic">“</span>Do not trust a company simply because it has a website, LinkedIn profile,
              registration certificate or attractive job offer. Verify the complete business relationship.<span className="text-[#ff3b30] not-italic">”</span>
            </blockquote>
          </Reveal>
        </div>
      </section>

      {/* Pricing + bank transfer + intake form (reused) */}
      <Verify />

      <Footer />
    </main>
  );
}
